import { Mic, MicOff } from 'lucide-react';
import { useSpeechInput, VOICE_LANGS } from '../../utils/speech';

/**
 * Dictation control for chat composers: a mic toggle plus a tiny language
 * cycle chip (EN → हिं → മല). Renders nothing when the browser has no
 * Web Speech API. Real <button>s so the analytics click listener works.
 */
export function MicButton({
  onFinal,
  track,
}: {
  /** Called with each final transcript chunk — append to the composer input. */
  onFinal: (text: string) => void;
  track?: string;
}) {
  const { isSupported, isListening, toggle, lang, setLang } = useSpeechInput({ onFinal });
  if (!isSupported) return null;

  const idx = VOICE_LANGS.findIndex((l) => l.code === lang);
  const current = VOICE_LANGS[idx] ?? VOICE_LANGS[0];
  const next = VOICE_LANGS[(idx + 1) % VOICE_LANGS.length];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={toggle}
        data-track={track}
        aria-label={isListening ? 'Stop dictation' : `Dictate in ${current.label}`}
        title={isListening ? 'Stop dictation' : `Speak instead of typing (${current.label})`}
        className={`p-2.5 border rounded-xl transition-colors ${
          isListening
            ? 'border-red-400 bg-red-50 text-red-600 animate-pulse'
            : 'border-legal-300 text-legal-500 hover:border-accent hover:text-accent-700'
        }`}
      >
        {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={() => setLang(next.code)}
        aria-label={`Dictation language: ${current.label}. Switch to ${next.label}`}
        title={`Dictation language: ${current.label} — tap for ${next.label}`}
        className="absolute -top-1.5 -right-1.5 min-w-[1.15rem] px-0.5 h-[1.15rem] rounded-full bg-legal-900 text-accent-300 text-[9px] font-semibold leading-[1.15rem] text-center"
      >
        {current.chip}
      </button>
    </div>
  );
}
